import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  ArrowLeft, MapPin, X, Upload, Trash2, Package, 
  Loader2, Save, ImagePlus
} from "lucide-react";
import api from "@/utils/api";
import { processMultipleImages, ACCEPTED_IMAGE_FORMATS } from "@/utils/imageUtils";

const EditListingPage = ({ user }) => {
  const { listingId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [categories, setCategories] = useState([]);
  const [listing, setListing] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "",
    price: "",
    condition: "good",
    images: [],
    shipping: {
      free_shipping: false,
      local_rate: "",
      national_rate: "",
      international_rate: "",
      handling_time: "1-3 business days"
    },
    location: null,
    local_pickup_available: false,
    local_pickup_address: "",
    variants: []
  });
  const [newImages, setNewImages] = useState([]);
  const [detectingLocation, setDetectingLocation] = useState(false);

  useEffect(() => {
    if (!user) {
      toast.error("Please login to edit listings");
      navigate('/');
      return;
    }
    
    const fetchData = async () => {
      try {
        const [listingRes, catRes] = await Promise.all([
          api.get(`/listings/${listingId}`),
          api.get("/categories")
        ]);
        
        const listingData = listingRes.data.listing;
        
        // Check ownership
        if (listingData.seller_id !== user.user_id) {
          toast.error("You can only edit your own listings");
          navigate('/dashboard');
          return;
        }
        
        // Check if editable
        if (listingData.status !== 'active' && listingData.status !== 'draft') {
          toast.error("This listing cannot be edited");
          navigate(`/item/${listingId}`);
          return;
        }
        
        setListing(listingData);
        setCategories(catRes.data);
        
        // Populate form with existing data
        setFormData({
          title: listingData.title || "",
          description: listingData.description || "",
          category: listingData.category || "",
          price: listingData.price?.toString() || "",
          condition: listingData.condition || "good",
          images: listingData.images || [],
          shipping: {
            free_shipping: listingData.shipping?.free_shipping || false,
            local_rate: listingData.shipping?.local_rate?.toString() || "",
            national_rate: listingData.shipping?.national_rate?.toString() || "",
            international_rate: listingData.shipping?.international_rate?.toString() || "",
            handling_time: listingData.shipping?.handling_time || "1-3 business days"
          },
          location: listingData.location || null,
          local_pickup_available: listingData.local_pickup_available || false,
          local_pickup_address: listingData.local_pickup_address || "",
          variants: (listingData.variants || []).map(v => ({
            tempId: v.variant_id || `tmp_${Math.random().toString(36).slice(2, 8)}`,
            variant_id: v.variant_id,
            color: v.color || '',
            size: v.size || '',
            quantity: v.quantity ?? '',
            price: v.price ?? '',
            image_index: (v.image && Array.isArray(listingData.images)) ? listingData.images.indexOf(v.image) : null
          }))
        });
      } catch (error) {
        console.error("Error fetching listing:", error);
        toast.error("Failed to load listing");
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [listingId, user, navigate]);

  const [locationInput, setLocationInput] = useState('');
  const [lookingUpLocation, setLookingUpLocation] = useState(false);

  const lookupLocation = async () => {
    if (!locationInput.trim()) {
      toast.error("Please enter a zip code or city name");
      return;
    }

    setLookingUpLocation(true);
    try {
      // Check if input looks like a US zip code (5 digits)
      const isUSZipCode = /^\d{5}(-\d{4})?$/.test(locationInput.trim());
      
      // Build the query - add "USA" for zip codes to avoid international confusion
      let searchQuery = locationInput.trim();
      if (isUSZipCode) {
        searchQuery = `${locationInput.trim()}, USA`;
      }
      
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(searchQuery)}&format=json&limit=1&addressdetails=1`
      );
      const data = await response.json();
      
      if (data && data.length > 0) {
        const result = data[0];
        const newLocation = {
          latitude: parseFloat(result.lat),
          longitude: parseFloat(result.lon),
          city: result.address?.city || result.address?.town || result.address?.village || result.address?.county || "",
          state: result.address?.state || "",
          country: result.address?.country || "",
          country_code: result.address?.country_code?.toUpperCase() || "",
          zip_code: result.address?.postcode || ""
        };
        
        // Debug log
        console.log('Location found:', JSON.stringify(newLocation, null, 2));
        
        setFormData(prev => ({
          ...prev,
          location: newLocation
        }));
        toast.success(`Location found: ${newLocation.city || newLocation.country} (lat: ${newLocation.latitude.toFixed(4)}, lng: ${newLocation.longitude.toFixed(4)})`);
        setLocationInput('');
      } else {
        toast.error("Location not found. Try a different format.");
      }
    } catch (error) {
      toast.error("Failed to lookup location");
    } finally {
      setLookingUpLocation(false);
    }
  };

  const detectLocation = async () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      return;
    }

    setDetectingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${position.coords.latitude}&lon=${position.coords.longitude}&format=json`
          );
          const data = await response.json();
          
          setFormData(prev => ({
            ...prev,
            location: {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              city: data.address?.city || data.address?.town || data.address?.village || "",
              state: data.address?.state || "",
              country: data.address?.country || "",
              country_code: data.address?.country_code?.toUpperCase() || "",
              zip_code: data.address?.postcode || ""
            }
          }));
          toast.success("Location updated!");
        } catch (error) {
          setFormData(prev => ({
            ...prev,
            location: {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              city: "",
              state: "",
              country: "",
              country_code: "",
              zip_code: ""
            }
          }));
        }
        setDetectingLocation(false);
      },
      () => {
        toast.error("Failed to detect location");
        setDetectingLocation(false);
      }
    );
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    const totalImages = formData.images.length + newImages.length;
    const remainingSlots = 10 - totalImages;
    
    if (remainingSlots <= 0) {
      toast.error("Maximum 10 images allowed");
      return;
    }
    
    setUploadingImages(true);
    try {
      const { images, errors } = await processMultipleImages(
        files.slice(0, remainingSlots),
        remainingSlots,
        (status) => toast.info(status, { duration: 1000 })
      );
      
      if (images.length > 0) {
        setNewImages(prev => [...prev, ...images]);
        toast.success(`${images.length} image(s) added`);
      }
      
      if (errors.length > 0) {
        errors.forEach(err => {
          toast.error(`Failed to process ${err.file}: ${err.error}`);
        });
      }
    } catch (error) {
      toast.error("Failed to process images");
    } finally {
      setUploadingImages(false);
      // Reset file input
      e.target.value = '';
    }
  };

  const removeExistingImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const removeNewImage = (index) => {
    setNewImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!formData.title || !formData.description || !formData.category || !formData.price) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSaving(true);
    try {
      // Combine existing and new images
      const allImages = [...formData.images, ...newImages];
      
      const updateData = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        price: parseFloat(formData.price),
        condition: formData.condition,
        images: allImages,
        shipping: {
          free_shipping: formData.shipping.free_shipping,
          local_rate: formData.shipping.local_rate ? parseFloat(formData.shipping.local_rate) : null,
          national_rate: formData.shipping.national_rate ? parseFloat(formData.shipping.national_rate) : null,
          international_rate: formData.shipping.international_rate ? parseFloat(formData.shipping.international_rate) : null,
          handling_time: formData.shipping.handling_time
        },
        location: formData.location,
        local_pickup_available: formData.local_pickup_available,
        local_pickup_address: formData.local_pickup_address,
        variants: (formData.variants || [])
          .filter(v => (v.color && v.color.trim()) || (v.size && v.size.trim()) || (v.quantity !== '' && v.quantity !== null && v.quantity !== undefined))
          .map(v => ({
            variant_id: v.variant_id || `var_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
            color: v.color?.trim() || null,
            size: v.size?.trim() || null,
            price: v.price !== '' && v.price !== null && v.price !== undefined ? parseFloat(v.price) : null,
            quantity: parseInt(v.quantity || 0, 10) || 0,
            image: (v.image_index !== null && v.image_index !== undefined && v.image_index >= 0)
              ? allImages[v.image_index] : null
          }))
      };

      // Debug log to verify location data
      console.log('Saving listing with location:', JSON.stringify(updateData.location, null, 2));

      await api.put(`/listings/${listingId}`, updateData);
      toast.success("Listing updated successfully!");
      navigate(`/item/${listingId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update listing");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-52 pb-16 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!listing) return null;

  const isAuction = listing.listing_type === "auction";
  const hasBids = listing.bid_count > 0;

  return (
    <div className="min-h-screen pt-52 pb-16 bg-slate-50" data-testid="edit-listing-page">
      <div className="container mx-auto px-4 md:px-6 max-w-3xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(`/item/${listingId}`)}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="font-outfit text-2xl font-bold">Edit Listing</h1>
            <p className="text-muted-foreground text-sm">Update your listing details</p>
          </div>
        </div>

        {/* Warning for auctions with bids */}
        {isAuction && hasBids && (
          <Card className="p-4 mb-6 bg-amber-50 border-amber-200">
            <p className="text-amber-800 text-sm">
              <strong>Note:</strong> This auction has {listing.bid_count} bid(s). You cannot change the price, but you can update other details.
            </p>
          </Card>
        )}

        <Card className="p-6">
          <div className="space-y-6">
            {/* Title */}
            <div>
              <Label className="mb-2 block">Title *</Label>
              <Input 
                placeholder="What are you selling?"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                data-testid="edit-title-input"
              />
            </div>

            {/* Category */}
            <div>
              <Label className="mb-2 block">Category *</Label>
              <Select 
                value={formData.category} 
                onValueChange={(v) => setFormData({ ...formData, category: v })}
              >
                <SelectTrigger data-testid="edit-category-select">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Description */}
            <div>
              <Label className="mb-2 block">Description *</Label>
              <Textarea 
                placeholder="Describe your item in detail"
                rows={4}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                data-testid="edit-description-input"
              />
            </div>

            {/* Price */}
            <div>
              <Label className="mb-2 block">
                {isAuction ? 'Starting Price' : 'Price'} *
                {isAuction && hasBids && <span className="text-muted-foreground ml-2">(Cannot be changed)</span>}
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <Input 
                  type="number"
                  placeholder="0.00"
                  className="pl-7"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  disabled={isAuction && hasBids}
                  min="0.01"
                  step="0.01"
                  data-testid="edit-price-input"
                />
              </div>
            </div>

            {/* Condition */}
            <div>
              <Label className="mb-2 block">Condition</Label>
              <Select 
                value={formData.condition} 
                onValueChange={(v) => setFormData({ ...formData, condition: v })}
              >
                <SelectTrigger data-testid="edit-condition-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="like_new">Like New</SelectItem>
                  <SelectItem value="excellent">Excellent</SelectItem>
                  <SelectItem value="good">Good</SelectItem>
                  <SelectItem value="fair">Fair</SelectItem>
                  <SelectItem value="used">Used</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Variants Section */}
            <div>
              <Label className="mb-2 block font-medium">Variants · Stock per Option</Label>
              <p className="text-sm text-muted-foreground mb-3">
                Optional. Add variants if this item comes in multiple colors / sizes with their own stock counts.
              </p>
              <div className="space-y-2">
                {formData.variants.map((v, idx) => {
                  const allImages = [...formData.images, ...newImages];
                  return (
                    <div key={v.tempId || idx} className="grid grid-cols-12 gap-2 items-center p-2 border rounded-lg bg-slate-50">
                      <Input
                        className="col-span-3"
                        placeholder="Color (e.g. Red)"
                        value={v.color || ''}
                        onChange={(e) => {
                          const next = [...formData.variants];
                          next[idx] = { ...next[idx], color: e.target.value };
                          setFormData({ ...formData, variants: next });
                        }}
                        data-testid={`edit-variant-color-${idx}`}
                      />
                      <Input
                        className="col-span-2"
                        placeholder="Size (S, M, 10)"
                        value={v.size || ''}
                        onChange={(e) => {
                          const next = [...formData.variants];
                          next[idx] = { ...next[idx], size: e.target.value };
                          setFormData({ ...formData, variants: next });
                        }}
                        data-testid={`edit-variant-size-${idx}`}
                      />
                      <Input
                        className="col-span-2"
                        type="number"
                        min="0"
                        placeholder="Qty"
                        value={v.quantity ?? ''}
                        onChange={(e) => {
                          const next = [...formData.variants];
                          next[idx] = { ...next[idx], quantity: e.target.value };
                          setFormData({ ...formData, variants: next });
                        }}
                        data-testid={`edit-variant-qty-${idx}`}
                      />
                      <Input
                        className="col-span-2"
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="Price (opt)"
                        value={v.price ?? ''}
                        onChange={(e) => {
                          const next = [...formData.variants];
                          next[idx] = { ...next[idx], price: e.target.value };
                          setFormData({ ...formData, variants: next });
                        }}
                      />
                      <select
                        className="col-span-2 h-9 rounded-md border border-input bg-background px-2 text-sm"
                        value={v.image_index ?? -1}
                        onChange={(e) => {
                          const idxVal = parseInt(e.target.value, 10);
                          const next = [...formData.variants];
                          next[idx] = { ...next[idx], image_index: idxVal === -1 ? null : idxVal };
                          setFormData({ ...formData, variants: next });
                        }}
                      >
                        <option value={-1}>No photo</option>
                        {allImages.map((_, i) => (
                          <option key={i} value={i}>Image {i + 1}</option>
                        ))}
                      </select>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="col-span-1 text-red-500 hover:text-red-600"
                        onClick={() => {
                          const next = formData.variants.filter((_, i) => i !== idx);
                          setFormData({ ...formData, variants: next });
                        }}
                      >
                        ×
                      </Button>
                    </div>
                  );
                })}
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    setFormData({
                      ...formData,
                      variants: [
                        ...formData.variants,
                        { tempId: `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, color: '', size: '', quantity: '', price: '', image_index: null }
                      ]
                    });
                  }}
                  data-testid="edit-add-variant-btn"
                >
                  + Add a variant
                </Button>
              </div>
            </div>

            {/* Images Section */}
            <div>
              <Label className="mb-2 block flex items-center gap-2">
                <ImagePlus className="h-4 w-4" />
                Photos ({formData.images.length + newImages.length}/10)
              </Label>
              
              {/* Existing Images */}
              {formData.images.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm text-muted-foreground mb-2">Current Images</p>
                  <div className="grid grid-cols-5 gap-2">
                    {formData.images.map((img, i) => (
                      <div key={`existing-${i}`} className="relative aspect-square rounded-lg overflow-hidden group border-2 border-emerald-200">
                        <img src={img} alt="" className="w-full h-full object-cover" />
                        <button 
                          className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity"
                          onClick={() => removeExistingImage(i)}
                          type="button"
                        >
                          <Trash2 className="h-5 w-5 text-white" />
                        </button>
                        {i === 0 && (
                          <span className="absolute bottom-1 left-1 text-xs bg-emerald-500 text-white px-1 rounded">Main</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* New Images */}
              {newImages.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm text-muted-foreground mb-2">New Images (will be added)</p>
                  <div className="grid grid-cols-5 gap-2">
                    {newImages.map((img, i) => (
                      <div key={`new-${i}`} className="relative aspect-square rounded-lg overflow-hidden group border-2 border-blue-200">
                        <img src={img} alt="" className="w-full h-full object-cover" />
                        <button 
                          className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity"
                          onClick={() => removeNewImage(i)}
                          type="button"
                        >
                          <Trash2 className="h-5 w-5 text-white" />
                        </button>
                        <span className="absolute bottom-1 left-1 text-xs bg-blue-500 text-white px-1 rounded">New</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Upload Button */}
              {formData.images.length + newImages.length < 10 && (
                <div 
                  className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                    uploadingImages 
                      ? 'border-emerald-400 bg-emerald-50 cursor-wait' 
                      : 'border-slate-300 cursor-pointer hover:border-emerald-400 hover:bg-emerald-50'
                  }`}
                  onClick={() => !uploadingImages && document.getElementById('edit-image-input').click()}
                >
                  {uploadingImages ? (
                    <>
                      <Loader2 className="h-8 w-8 mx-auto text-emerald-500 mb-2 animate-spin" />
                      <p className="text-sm text-emerald-600">Processing images...</p>
                    </>
                  ) : (
                    <>
                      <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                      <p className="text-sm text-muted-foreground">Click to add more images</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {10 - formData.images.length - newImages.length} slots remaining • Supports JPG, PNG, HEIC
                      </p>
                    </>
                  )}
                  <input 
                    id="edit-image-input"
                    type="file"
                    accept={ACCEPTED_IMAGE_FORMATS}
                    multiple
                    className="hidden"
                    onChange={handleImageUpload}
                    disabled={uploadingImages}
                    data-testid="edit-image-upload"
                  />
                </div>
              )}
            </div>

            {/* Location */}
            <div>
              <Label className="mb-2 block">Location</Label>
              {formData.location ? (
                <div className={`p-4 rounded-xl border ${
                  formData.location.latitude && formData.location.longitude 
                    ? 'bg-emerald-50 border-emerald-200' 
                    : 'bg-orange-50 border-orange-200'
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <MapPin className={`h-5 w-5 ${
                        formData.location.latitude && formData.location.longitude 
                          ? 'text-emerald-600' 
                          : 'text-orange-600'
                      }`} />
                      <div>
                        <p className={`font-medium ${
                          formData.location.latitude && formData.location.longitude 
                            ? 'text-emerald-900' 
                            : 'text-orange-900'
                        }`}>
                          {[
                            formData.location.city,
                            formData.location.state,
                            formData.location.zip_code
                          ].filter(Boolean).join(', ') || 
                          formData.location.country || 
                          "Location set"}
                        </p>
                        <p className={`text-xs ${
                          formData.location.latitude && formData.location.longitude 
                            ? 'text-emerald-700' 
                            : 'text-orange-700'
                        }`}>
                          {formData.location.country}
                        </p>
                        {/* Show map status */}
                        {formData.location.latitude && formData.location.longitude ? (
                          <p className="text-xs text-emerald-600 mt-1">✓ Will appear on map</p>
                        ) : (
                          <p className="text-xs text-orange-600 mt-1">⚠ Missing coordinates - click X and re-enter location to fix</p>
                        )}
                      </div>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setFormData({ ...formData, location: null })}
                      type="button"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Zip Code / City Input */}
                  <div className="flex gap-2">
                    <Input 
                      placeholder="Enter zip code or city, state"
                      value={locationInput}
                      onChange={(e) => setLocationInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), lookupLocation())}
                      className="flex-1"
                    />
                    <Button 
                      variant="default"
                      className="bg-emerald-500 hover:bg-emerald-600"
                      onClick={lookupLocation}
                      disabled={lookingUpLocation}
                      type="button"
                    >
                      {lookingUpLocation ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        "Lookup"
                      )}
                    </Button>
                  </div>
                  
                  {/* Or use GPS */}
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-px bg-slate-200" />
                    <span className="text-xs text-muted-foreground">or</span>
                    <div className="flex-1 h-px bg-slate-200" />
                  </div>
                  
                  <Button 
                    variant="outline" 
                    className="w-full gap-2"
                    onClick={detectLocation}
                    disabled={detectingLocation}
                    type="button"
                  >
                    <MapPin className="h-4 w-4" />
                    {detectingLocation ? "Detecting..." : "Use My Current Location"}
                  </Button>
                </div>
              )}
            </div>

            {/* Shipping Options */}
            <div className="border-t pt-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Package className="h-5 w-5 text-emerald-500" />
                Shipping Options
              </h3>
              
              {/* Free Shipping Toggle */}
              <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg mb-4">
                <div>
                  <p className="font-medium">Free Shipping</p>
                  <p className="text-xs text-muted-foreground">Offer free shipping to attract more buyers</p>
                </div>
                <button
                  type="button"
                  onClick={() => setFormData({
                    ...formData,
                    shipping: { ...formData.shipping, free_shipping: !formData.shipping.free_shipping }
                  })}
                  className={`w-12 h-6 rounded-full transition ${formData.shipping.free_shipping ? 'bg-emerald-500' : 'bg-gray-300'}`}
                >
                  <span className={`block w-5 h-5 bg-white rounded-full shadow transform transition ${formData.shipping.free_shipping ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
              </div>

              {/* Shipping Rates */}
              {!formData.shipping.free_shipping && (
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs mb-1 block">Local</Label>
                    <div className="relative">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                      <Input 
                        type="number"
                        placeholder="5.00"
                        className="pl-6 h-9 text-sm"
                        value={formData.shipping.local_rate}
                        onChange={(e) => setFormData({
                          ...formData,
                          shipping: { ...formData.shipping, local_rate: e.target.value }
                        })}
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs mb-1 block">National</Label>
                    <div className="relative">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                      <Input 
                        type="number"
                        placeholder="12.00"
                        className="pl-6 h-9 text-sm"
                        value={formData.shipping.national_rate}
                        onChange={(e) => setFormData({
                          ...formData,
                          shipping: { ...formData.shipping, national_rate: e.target.value }
                        })}
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs mb-1 block">International</Label>
                    <div className="relative">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                      <Input 
                        type="number"
                        placeholder="25.00"
                        className="pl-6 h-9 text-sm"
                        value={formData.shipping.international_rate}
                        onChange={(e) => setFormData({
                          ...formData,
                          shipping: { ...formData.shipping, international_rate: e.target.value }
                        })}
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Local Pickup */}
              <div className="mt-4 flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <div>
                  <p className="font-medium flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-blue-500" />
                    Local Pickup
                  </p>
                  <p className="text-xs text-muted-foreground">Allow buyers to pick up in person</p>
                </div>
                <button
                  type="button"
                  onClick={() => setFormData({
                    ...formData,
                    local_pickup_available: !formData.local_pickup_available
                  })}
                  className={`w-12 h-6 rounded-full transition ${formData.local_pickup_available ? 'bg-blue-500' : 'bg-gray-300'}`}
                >
                  <span className={`block w-5 h-5 bg-white rounded-full shadow transform transition ${formData.local_pickup_available ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
              </div>
              
              {formData.local_pickup_available && (
                <div className="mt-3">
                  <Label className="text-xs mb-1 block">Pickup Location</Label>
                  <Input 
                    placeholder="e.g., Downtown Los Angeles, CA"
                    className="h-9 text-sm"
                    value={formData.local_pickup_address}
                    onChange={(e) => setFormData({ ...formData, local_pickup_address: e.target.value })}
                  />
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4 border-t">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => navigate(`/item/${listingId}`)}
              >
                Cancel
              </Button>
              <Button 
                className="flex-1 bg-emerald-500 hover:bg-emerald-600 gap-2"
                onClick={handleSave}
                disabled={saving}
                data-testid="save-listing-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default EditListingPage;
